import React, { useEffect, useMemo, useState } from 'react';
import { Button, Groupper, Header, Icon, Label, Message, Modal, Placeholder, Table, Toast, Toolbar } from 'react-frontier';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { CardBrand, Payout, SetLoading } from '../DashboardClasses';
import moment from 'moment';
import { addCommas, bindClose, getCardBrandIcon } from '@payfront/components/Util';
import API from '../API';
import NotFound from './NotFound';
import { useUser } from '../Hooks';

interface PayoutDetails extends Payout{
	description: string,
	amount: number,
	commission_amount: number,
	release_amount: number,
	retention_amount: number,
	release_payout: number,
	date_retention_release: number,
	payments: {
		payment_id: number,
		total: number,
		commission_client: number,
		card_digits: number,
		card_brand: CardBrand,
		date_created: number,
		date_paid: number,
	}[],
	refunds: {
		refund_id: number,
		payment_id: number,
		amount: number,
		date_created: number,
	}[],
	releases: {
		payout_id: number,
		amount: number,
		release_payout: number,
		date_release: number,
		date_created: number,
	}[]
}

enum ShownModal{
	COMPELTE = 1,
	REJECT = 2,
}

var PayoutView = ()=>{
	var { t } = useTranslation();
	var { user } = useUser();
	var params = useParams();
	var [payout, setPayout] = useState<PayoutDetails>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [shownModal, setShownModal] = useState<ShownModal>(null);

	var payout_id = useMemo(()=>{
		if(!params || !params.id) return null;
		var payout_id = parseInt(params.id);
		if(Number.isNaN(payout_id)) return null;
		return payout_id;
	}, [params]);

	useEffect(()=>{
		if(!payout_id || payout) return;
		API.getPayout(payout_id, true, true, true).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPayout(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_error', { code: 'PYOGT-1' }));
		});
	}, []);

	var sendComplete = (setLoading: SetLoading)=>{
		if(!user.admin) return;
		setLoading(true);
		setModalPrompts([]);
		API.completePayout(payout.payout_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setPayout({
				...payout,
				paid: true,
				date_paid: moment().unix()
			});
			setShownModal(null);
			Toast.success(t('payout_view.complete.toast'));
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_error', { code: 'PYOCPL-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	if(!payout_id) return <NotFound />
	if(loadError){
		return <Header text={t('common.error')} subtext={loadError} style={{ marginTop: 30 }} />
	}
	
	return <div className="pf payout-view-screen">
		<Groupper title={t('payout_view.title', { id: payout_id })} className='details'>
			{(!!user.admin && payout && !payout.paid && !payout.cancelled) && (
				<Toolbar stretch>
					<Toolbar.Item text={t('payout_view.complete.button')} iconName='check' onClick={bindClose(setShownModal, ShownModal.COMPELTE)} style={{ color: 'green', fontWeight: 'bold' }} />
					<Toolbar.Item text={t('common.cancel')} iconName='times' onClick={bindClose(setShownModal, ShownModal.REJECT)} style={{ color: 'brown', fontWeight: 'bold' }} />
				</Toolbar>
			)}
			<Table details striped fitted className='last'>
				<Table.Row data={[t('common.id'), payout_id]} />
				<Table.Row data={[t('payout_view.details.description'), (
					<Placeholder value={payout?.description} />
				)]} />
				<Table.Row data={[t('common.date_created'), (
					<Placeholder value={payout ? moment.unix(payout.date_created).format('DD/MMM/YY') : null} random={false} width={120} />
				)]} />
				<Table.Row data={[t('common.status'), (
					<Placeholder value={payout ? (
						<Label color={payout.cancelled ? 'red' : (payout.paid ? 'green' : 'blue')}>
							{t(payout.cancelled ? 'common.cancelled' : (payout.paid ? 'common.transfered' : 'common.pending'))}
						</Label>
					) : null} random={false} width={100} />
				)]} />
				{payout?.paid ? (
					<Table.Row data={[t('payout_view.details.date_paid'), moment.unix(payout.date_paid).format('DD/MMM/YY HH:mm')]} />
				) : null}
				<Table.Divider />
				<Table.Row data={[t('payout_view.details.amount_total'), (
					<Placeholder value={addCommas(payout?.amount)} width={40} />
				)]} />
				<Table.Row data={[t('payout_view.details.commission'), (
					<Placeholder value={payout ? <>
						-${addCommas(payout?.commission_amount)}
						<div className="meta">${addCommas(payout.amount-payout.commission_amount)}</div>
					</> : null} width={40} />
				)]} />
				{(!payout || (!!payout?.release_amount && payout?.release_amount>0)) && (
					<Table.Row data={[t('payout_view.details.retention_release'), (
						<Placeholder value={payout ? payout.cancelled ? (
							<Icon name='minus' color='gray' />
						) : <>
							+${addCommas(payout?.release_amount)}
							<div className="meta">${addCommas(payout.amount-payout.commission_amount+payout.release_amount)}</div>
						</> : null} width={40} />
					)]} />
				)}
				{(!payout || (!!payout?.retention_amount && payout?.retention_amount>0)) && (
					<Table.Row data={[t('payout_view.details.retention'), (
						<Placeholder value={payout ? <>
							-${addCommas(payout?.retention_amount)}
							<div className="meta">${addCommas(payout.amount-payout.commission_amount+payout.release_amount-payout.retention_amount)}</div>
						</> : null} width={40} />
					)]} />
				)}
				<Table.Row>
					<Table.Cell>{t('payout_view.details.amount_paid')}</Table.Cell>
					<Table.Cell style={payout ? { color: 'green', fontWeight: 'bold', fontSize: 16, lineHeight: '20px' } : null}>
						<Placeholder value={payout ? <>
							${addCommas(payout?.amount_paid)}
						</> : null} width={40} />
					</Table.Cell>
				</Table.Row>
			</Table>
		</Groupper>

		<Table 
			striped
			title='Pagos' 
			headers={[t('common.id'), t('common.date'), t('common.payment'), t('common.amount'), t('common.commission'), t('common.income')]}
			collapsingIndexes={[0, 2, 3, 4, 5]}
		>
			{payout ? (
				!(payout.payments || payout.payments.length==0) && (!payout.releases || payout.releases.length==0) && (!payout.refunds || payout.refunds.length==0) ? (
					<Table.Cell row empty colSpan={10}>{t('payout_view.no_payments')}</Table.Cell>
				) : (
					<>
						{!!payout.payments && payout.payments.map(a=>(
							<Table.Row selectable compact key={`PYPM-${a.payment_id}`} as={Link} to={`/pagos/${a.payment_id}`} data={[
								a.payment_id,
								moment.unix(a.date_paid || a.date_created).format('DD/MM/YY HH:mm'),
								<>
									<Icon name={getCardBrandIcon(a.card_brand)} brand />
									{('0000'+a.card_digits).slice(-4)}
								</>,
								`$${addCommas(a.total)}`,
								`$${addCommas(a.commission_client)}`,
								<b>${addCommas(a.total-a.commission_client)}</b>,
							]} />
						))}
						{payout.refunds && payout.refunds.map(a=>(
							<Table.Row selectable compact key={`PYRF-${a.refund_id}`} as={Link} to={`/pagos/${a.payment_id}`}>
								<Table.Cell>{a.refund_id}</Table.Cell>
								<Table.Cell>{moment.unix(a.date_created).format('DD/MM/YY HH:mm')}</Table.Cell>
								<Table.Cell colSpan={3}>
									<Label color='orange'>{t('common.refund')}</Label>
								</Table.Cell>
								<Table.Cell style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
									-${addCommas(a.amount)}
								</Table.Cell>
							</Table.Row>
						))}
						{payout.releases && payout.releases.map(a=>(
							<Table.Row selectable compact key={`PYRL-${a.payout_id}`} as={Link} to={`/payouts/${a.payout_id}`}>
								<Table.Cell>{a.payout_id}</Table.Cell>
								<Table.Cell>{moment.unix(a.date_release).format('DD/MM/YY HH:mm')}</Table.Cell>
								<Table.Cell colSpan={3}>
									<Label color='green'>{t('payout_view.release')}</Label>
								</Table.Cell>
								<Table.Cell>
									<b>${addCommas(a.amount)}</b>
								</Table.Cell>
							</Table.Row>
						))}
					</>
				)
			) : (
				new Array(10).fill('0').map((a, i)=>(
					<Table.Row key={`PYPLH-${i}`} data={[
						<Placeholder width={75} random={false} />,
						<Placeholder width={120} random={false} />,
						<Placeholder width={80} random={false} />,
						<Placeholder width={50} variance={15} />,
						<Placeholder width={50} variance={20} />,
						<Placeholder width={30} />,
					]} />
				))
			)}
		</Table>
		{!!user.admin && !!payout && !payout.cancelled && (
			<Modal open={shownModal===ShownModal.COMPELTE} size='tiny' onClose={bindClose(setShownModal)}>
				<Modal.Header text={t('payout_view.complete.title')} />
				<Modal.Content>
					<Header text={t('payout_view.complete.confirm_header')} size='small' subtext={t('payout_view.complete.confirm_subheader')} />
					<Table details striped title='Liquidación' style={{ marginTop: 15 }} titleSize='small' data={[
						[t('common.id'), payout.payout_id],
						[t('common.client'), payout.client_name],
						[t('common.amount'), <>
							<b>{addCommas(payout.amount_paid)}</b>
						</>],
					]}/>
					<Message type='error' list={modalPrompts} />
				</Modal.Content>
				<Modal.Actions>
					<Button text={t('common.close')} basic onClick={bindClose(setShownModal)} />
					<Button text={t('payout_view.complete.button')} color='blue' onClick={sendComplete} />
				</Modal.Actions>
			</Modal>
		)}
	</div>
}

export default PayoutView;